import Header from 'layout/Header';
import { withPlaceholder } from 'HOC';
import { useIraAccountForm } from 'hooks/forms/useIraAccountsForm';
import {
  useEditIndependentIraUser,
  useGetIndependentIraUser,
} from 'hooks/api/independentIra';
import { useGetProfileList } from 'hooks/api/profiles';
import { useUnsaveChangesModal } from 'hooks/modals';
import { useGetProfile } from 'hooks/api/login';
import { useGetDefaultUserManagers } from 'hooks/api/userManager';
import { getDirtyFieldValues } from 'utils/form';
import { formatInputDateForBE, mapCustodianTypeValueToKey, parseInputDate } from 'utils';
import { USER_TYPE } from 'constants/userTypes';
import { EntityStatus } from 'components/Label';
import StatusUserButton from 'components/StatusUserButton';
import Button from 'components/Button';
import SaveDiscardingChangesModal from 'components/UnsaveChangesModal';
import IndependentIraForm from '../components/IndependentIraForm';
import { mapIndependentIraTypeValueToKey } from '../IraIndependentIra.utils';
import IndependentIraNavTabs from '../components/IndependentIraNavTabs';

import styles from './IndependentIraInfo.module.css';
import messages from './IndependentIraInfo.messages';

const IndependentIraUserFormWithPlaceholders = withPlaceholder(IndependentIraForm);

export default function IndependentIraInfo() {
  const {
    data: independentIraUser,
    isLoading: isUserLoading,
    isError,
    refetch,
    isRefetching,
  } = useGetIndependentIraUser();
  const { mutateAsync: editIndependentIraUser, isLoading: isEditLoading } =
    useEditIndependentIraUser();
  const { data: otherAccounts } = useGetProfileList(independentIraUser?.email);
  const { data: profile } = useGetProfile();
  const { defaultEtzFee, defaultCustodyFeePercentageIraClub } = profile || {};
  const { data: defaultUserManagersData } = useGetDefaultUserManagers();

  const form = useIraAccountForm(independentIraUser);
  const { isDirty, dirtyFields } = form.formState;

  const unsaveChangesModal = useUnsaveChangesModal(
    form,
    form.reset.bind(null, {
      ...independentIraUser,
      annualFee: !!independentIraUser?.annualFee
        ? String(independentIraUser.annualFee)
        : undefined,
      custodyFeeAbsolute: !!independentIraUser?.custodyFeeAbsolute
        ? String(independentIraUser.custodyFeeAbsolute)
        : undefined,
      custodyFeePercentage: !!independentIraUser?.custodyFeePercentage
        ? String(independentIraUser.custodyFeePercentage)
        : undefined,
    }),
  );

  const onEdit = async (): Promise<void> => {
    const values = form.getValues();
    const dirtyFieldValues = getDirtyFieldValues(dirtyFields, {
      ...values,
      profileType: mapIndependentIraTypeValueToKey(values.profileType) ?? '',
      custodianType: mapCustodianTypeValueToKey(values.custodianType) ?? '',
      annualFee: +values.annualFee,
      custodyFeeAbsolute: values.custodyFeeAbsolute ?? '',
      custodyFeePercentage: values.custodyFeePercentage ?? '',
      nextBillingDate: values.nextBillingDate
        ? formatInputDateForBE(parseInputDate(values.nextBillingDate))
        : '',
    });

    await editIndependentIraUser(dirtyFieldValues);
    form.reset(values);
  };

  return (
    <>
      <Header
        title={
          independentIraUser
            ? `${independentIraUser?.firstName} ${independentIraUser?.middleName ?? ''} ${
                independentIraUser?.lastName
              }`
            : ''
        }
        complexComponent={true}
        label={{
          status: independentIraUser?.profileStatus,
          entityStatus: EntityStatus.User,
          isAccountClosed: independentIraUser?.accountClosed,
        }}
      >
        {independentIraUser && isDirty && (
          <>
            <Button
              className={styles.iraAccountsProfile__buttonCancel}
              variant={'outline'}
              onClick={unsaveChangesModal.onCancelClick}
            >
              {messages.cancelButton}
            </Button>
            <Button
              className={styles.iraAccountsProfile__buttonSave}
              isLoading={isEditLoading}
              type={'submit'}
              onClick={form.handleSubmit(onEdit)}
            >
              {messages.saveButton}
            </Button>
          </>
        )}
        {independentIraUser && !isDirty && (
          <StatusUserButton
            user={independentIraUser}
            userType={USER_TYPE.independentIra}
          />
        )}
      </Header>
      <SaveDiscardingChangesModal {...unsaveChangesModal} />

      <IndependentIraNavTabs />
      <IndependentIraUserFormWithPlaceholders
        isLoading={isUserLoading}
        isError={isError}
        refetch={refetch}
        isRefetching={isRefetching}
        {...independentIraUser}
        profileType={independentIraUser?.profileType}
        lastActivity={independentIraUser?.lastLogin}
        form={form}
        isNew={false}
        profileList={otherAccounts}
        defaultEtzFee={defaultEtzFee}
        defaultCustodyFeePercentageIraClub={defaultCustodyFeePercentageIraClub}
        defaultUserManager={defaultUserManagersData}
      />
    </>
  );
}
